import {redo, undo} from "y-prosemirror";
import {undo as historyUndo, redo as historyRedo} from "prosemirror-history";
import {keymap} from "prosemirror-keymap";
import {baseKeymap, setBlockType, toggleMark} from "prosemirror-commands";
import {liftListItem, sinkListItem, wrapInList} from "prosemirror-schema-list";
import {
    createDedentListCommand,
    createIndentListCommand,
    createToggleListCommand,
    listKeymap,
} from "prosemirror-flat-list";
import {goToNextCell} from "prosemirror-tables";
import {applyLink, getSelectedLinkAttrs, isSelectionLink} from "../../components/toolbar/toolbarUtils";

function createUndoRedoKeymap(collaborationEnabled) {
    if (collaborationEnabled) {
        return keymap({
            "Mod-z": undo,
            "Mod-y": redo,
            "Mod-Shift-z": redo,
        });
    }

    return keymap({
        "Mod-z": historyUndo,
        "Mod-y": historyRedo,
        "Mod-Shift-z": historyRedo,
    });
}

function createListKeymap() {
    return keymap({
        // Flat-list commands — handle Enter, Backspace, Delete, Mod-[, Mod-]
        ...listKeymap,
        // Tab / Shift-Tab: prefer table navigation first, then fall back to list indent
        Tab: (state, dispatch, view) => {
            if (goToNextCell(1)(state, dispatch)) return true;
            return createIndentListCommand()(state, dispatch, view);
        },
        "Shift-Tab": (state, dispatch, view) => {
            if (goToNextCell(-1)(state, dispatch)) return true;
            return createDedentListCommand()(state, dispatch, view);
        },
        // Legacy list node support (for documents not yet migrated to flat-list)
        "Shift-Ctrl-8": (state, dispatch) => {
            const listNode = state.schema.nodes.list;
            if (listNode) {
                return createToggleListCommand({kind: "bullet"})(state, dispatch);
            }
            return wrapInList(state.schema.nodes.bullet_list)(state, dispatch);
        },
        "Shift-Ctrl-9": (state, dispatch) => {
            const listNode = state.schema.nodes.list;
            if (listNode) {
                return createToggleListCommand({kind: "ordered"})(state, dispatch);
            }
            return wrapInList(state.schema.nodes.ordered_list)(state, dispatch);
        },
    });
}

function createFormattingKeymap() {
    return keymap({
        "Mod-b": (state, dispatch) => {
            const strong = state.schema.marks.strong;
            return strong ? toggleMark(strong)(state, dispatch) : false;
        },
        "Mod-i": (state, dispatch) => {
            const em = state.schema.marks.em;
            return em ? toggleMark(em)(state, dispatch) : false;
        },
        "Mod-k": (state, dispatch) => {
            const link = state.schema.marks.link;
            if (!link || state.selection.empty) return false;

            const currentLink = isSelectionLink(state) ? getSelectedLinkAttrs(state)?.href : null;
            const href = window.prompt("Enter URL", currentLink || "https://");
            if (!href) return true;

            return applyLink(state, dispatch, href);
        },
    });
}

function createHeadingKeymap() {
    return keymap({
        "Mod-Alt-0": (state, dispatch) => {
            const paragraph = state.schema.nodes.paragraph;
            return paragraph ? setBlockType(paragraph)(state, dispatch) : false;
        },
        "Mod-Alt-1": (state, dispatch) => {
            const heading = state.schema.nodes.heading;
            return heading ? setBlockType(heading, {level: 1})(state, dispatch) : false;
        },
        "Mod-Alt-2": (state, dispatch) => {
            const heading = state.schema.nodes.heading;
            return heading ? setBlockType(heading, {level: 2})(state, dispatch) : false;
        },
        "Mod-Alt-3": (state, dispatch) => {
            const heading = state.schema.nodes.heading;
            return heading ? setBlockType(heading, {level: 3})(state, dispatch) : false;
        },
        "Mod-Alt-4": (state, dispatch) => {
            const heading = state.schema.nodes.heading;
            return heading ? setBlockType(heading, {level: 4})(state, dispatch) : false;
        },
        "Mod-Alt-5": (state, dispatch) => {
            const heading = state.schema.nodes.heading;
            return heading ? setBlockType(heading, {level: 5})(state, dispatch) : false;
        },
        "Mod-Alt-6": (state, dispatch) => {
            const heading = state.schema.nodes.heading;
            return heading ? setBlockType(heading, {level: 6})(state, dispatch) : false;
        },
    });
}

export function createInputKeymaps({collaborationEnabled}) {
    return [
        createUndoRedoKeymap(collaborationEnabled),
        createListKeymap(),
        createFormattingKeymap(),
        createHeadingKeymap(),
        keymap(baseKeymap),
    ];
}

